'use client';
import React from 'react';
/**
 * BookingModal – the dialog every "Talk to us" opens.
 *
 * It opens on the "Talk to Nathan" form (`openModal`), or straight on Nathan's
 * Google appointment schedule (`openCalendar`, the report's "Book a 15m call").
 * The two views lead to each other without submitting anything: the form's
 * footer "Book a 15m call instead" and the calendar's footer pill "Message
 * Nathan instead". Typed values survive the round trip because both views live
 * in this one component.
 *
 * Why the form comes first: when the modal offered both, four of five people
 * chose to leave a request over booking, and an abandoned calendar captures no
 * email. Approved design: `.claude/design-specs/2026-11-01-talk-to-us-note-first.html`.
 *
 * On an unlocked report the form drops its email and name fields (the unlock
 * already gave us the address; the server resolves it from the check), so the
 * only input is an optional note and the first tap can send.
 *
 * Sub-components are extracted into collocated files:
 * - BookingModal.types.ts     - Shared type definitions
 * - BookingModal.anatomy.tsx  - Header row, footer band and footer link shared by every view
 * - BookingModal.calendar.tsx - CalendarContent (inline appointment schedule)
 * - BookingModal.form.tsx     - FormContent (the Talk to Nathan form)
 * - BookingModal.success.tsx  - SuccessContent (confirmation view)
 *
 * @module
 */


import { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

import type { BookingModalEntry, ModalStatus } from './BookingModal.types';
import { CalendarContent } from './BookingModal.calendar';
import { FormContent } from './BookingModal.form';
import { SuccessContent } from './BookingModal.success';
import { bookingEvents, bookingOfferEvents } from '@/components/PostHogProvider';
import {
  BookingRequestSubmissionSchema,
  type BookingModalRequest,
  type BookingRequestSubmission,
} from '@marketing-optimizer/ontology';
import { resolveBookingSource } from '@/lib/booking-attribution';
import { readAdAttribution } from '@/lib/ad-attribution';

/** The first field of the form view: the email on marketing pages, the note on a report. */
const FIRST_FIELD_SELECTOR = 'input:not([tabindex="-1"]), textarea';

// =============================================================================
// Types
// =============================================================================

interface BookingModalProps {
  /** An offer or manual-review request (`openRequest`). */
  request?: BookingModalRequest;
  /** A "Talk to us" or "Book a 15m call" open (`openModal` / `openCalendar`). */
  entry?: BookingModalEntry;
  isOpen: boolean;
  onClose: () => void;
}

// =============================================================================
// Email Validation
// =============================================================================

/** Regex pattern for basic email validation. Matches: anything@anything.anything */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates an email address using a basic regex pattern.
 *
 * This is a client-side validation check only. The server should perform
 * more comprehensive validation (RFC 5321/5322 compliance, etc.).
 *
 * @param {string} email - The email address to validate
 * @returns {boolean} True if email matches basic pattern, false otherwise
 *
 * @example
 * ```tsx
 * if (isValidEmail(email)) {
 *   // Submit form
 * }
 * ```
 */
function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}


// =============================================================================
// BookingModal Component
// =============================================================================

/**
 * Key behaviours:
 * - **Honeypot Protection**: Hidden field to catch simple bots
 * - **Accessibility**: Escape closes, focus is trapped and restored, and a
 *   view switch moves focus (the calendar's title, or the form's first field)
 * - **Attribution**: first-touch UTMs, referrer and page path ride with the lead
 */
export function BookingModal({ isOpen, onClose, request, entry }: BookingModalProps): React.ReactElement {
  // A report-question request from `openRequest` is treated like a report open without a remembered email.
  const report = entry?.report ?? (request?.kind === 'report-question' ? { checkId: request.checkId } : undefined);
  const initialView = entry?.start === 'calendar' ? 'calendar' : 'form';
  const [view, setView] = useState<'calendar' | 'form'>(initialView);
  // How this open first reached the calendar, recorded on `booking_calendar_viewed`.
  const [calendarEntry, setCalendarEntry] = useState<'direct' | 'request-form'>('direct');
  const calendarViewed = useRef(false);
  const [location, setLocation] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState<ModalStatus>('idle');
  const [error, setError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const reduceMotion = useReducedMotion();

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Delay reset to allow animation to complete
      const timer = setTimeout(() => {
        setEmail('');
        setName('');
        setLocation('');
        setMessage('');
        setHoneypot('');
        setStatus('idle');
        setError('');
        setView(initialView);
        setCalendarEntry('direct');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialView]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus trap: keep focus within modal when open. Declared before the view
  // effect below so the opener is saved before focus moves into the dialog.
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    previousActiveElement.current = document.activeElement as HTMLElement;

    const getFocusable = (): HTMLElement[] => Array.from(modalRef.current?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    ) ?? []).filter((element) => element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true');

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];
      if (!modalRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? lastFocusable : firstFocusable)?.focus();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable || document.activeElement?.id === 'booking-modal-title') {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      // Restore focus when modal closes
      previousActiveElement.current?.focus();
    };
  }, [isOpen]);

  // On open and on every view switch: the title of the calendar or of a sent
  // confirmation (so a screen reader hears where it landed), or the form's first field.
  useEffect(() => {
    if (!isOpen) return;
    const root = modalRef.current;
    if (view === 'calendar' || status === 'success') root?.querySelector<HTMLElement>('#booking-modal-title')?.focus();
    else root?.querySelector<HTMLElement>(FIRST_FIELD_SELECTOR)?.focus();
    // `status` is deliberately absent: a failed submit must not yank focus back to the first field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, view]);

  // Once per open, when the calendar is first on screen (HAL-2083).
  useEffect(() => {
    if (!isOpen) {
      calendarViewed.current = false;
      return;
    }
    if (view !== 'calendar' || calendarViewed.current) return;
    calendarViewed.current = true;
    const pagePath = window.location.pathname;
    const source = resolveBookingSource(pagePath);
    bookingEvents.calendarViewed({
      pagePath,
      placement: entry?.placement ?? (request ? `offer-${request.kind}` : 'unknown'),
      entry: calendarEntry,
      ...(source && { source }),
    });
  }, [isOpen, view, entry, request, calendarEntry]);

  const handleBookCall = useCallback(() => {
    setCalendarEntry((current) => (calendarViewed.current ? current : 'request-form'));
    setView('calendar');
  }, []);

  const handleMessage = useCallback(() => {
    bookingOfferEvents.clicked({
      offer: report ? 'report-question' : 'request',
      placement: 'calendar-modal',
      pagePath: window.location.pathname,
    });
    setView('form');
  }, [report]);

  // Submission removes the focused button; move focus to the confirmation's title.
  useEffect(() => {
    if (status === 'success') modalRef.current?.querySelector<HTMLElement>('#booking-modal-title')?.focus();
  }, [status]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /**
   * Sends the message. A report message carries no email: the server takes
   * the reply address from the check's unlock lead. On success it captures
   * `booking_lead_submitted` and shows the confirmation.
   *
   * @fires /api/booking/capture-lead - POST request with form data and tracking params
   * @fires booking_lead_submitted - PostHog capture on success
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');

      const trimmedEmail = email.trim();
      const note = message.trim();
      const emailError = report
        ? ''
        : !trimmedEmail
          ? 'Please enter your email'
          : !isValidEmail(trimmedEmail) ? 'Please enter a valid email' : '';

      // The modal collects the note, the manual-review location and the report
      // message after opening; the server fills everything else from the saved check.
      const context: BookingRequestSubmission | undefined = report
        ? { kind: 'report-question', checkId: report.checkId, message: note }
        : request?.kind === 'manual-review' ? { ...request, location }
          : request?.kind === 'consultation' ? { ...request, ...(note && { message: note }) }
            : note ? { kind: 'note', message: note } : undefined;
      const parsedContext = context ? BookingRequestSubmissionSchema.safeParse(context) : undefined;
      const contextIssue = parsedContext && !parsedContext.success ? parsedContext.error.issues[0] : undefined;
      if (emailError || contextIssue) {
        setError(emailError || contextIssue?.message || 'Please check your details');
        return;
      }
      setStatus('loading');

      try {
        // UTMs come from the first-touch store, not this page's URL, so a
        // booking made after the visitor has left the ad's landing page (to a
        // report, then back to the site) still carries the click that brought
        // them in (HAL-1969). Only with storage blocked does it fall back to
        // this page's own parameters.
        const attribution = readAdAttribution();
        // Read once: the lead row and the PostHog event must agree on the
        // page that produced this booking.
        const pagePath = window.location.pathname;

        const response = await fetch('/api/booking/capture-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(!report && { email: trimmedEmail, name: name.trim() || undefined }),
            honeypot,
            referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
            utmSource: attribution?.utm_source,
            utmMedium: attribution?.utm_medium,
            utmCampaign: attribution?.utm_campaign,
            pagePath,
            ...(parsedContext?.success && { requestContext: parsedContext.data }),
          }),
        });

        const data = await response.json();

        if (response.ok && data.calendarUrl) {
          // Only the success branch fires, so a failed submit never looks like a lead.
          bookingEvents.submitted({
            pagePath,
            intent: 'request-only',
            source: resolveBookingSource(pagePath),
            ...(report ? { offer: 'report-question' }
              : request && { offer: request.kind === 'consultation' ? request.offer : request.kind }),
            ...(request?.kind === 'manual-review' && { failureCode: request.failureCode }),
          });
          setStatus('success');
        } else {
          setError(data.error || 'Something went wrong');
          setStatus('error');
        }
      } catch {
        setError('Something went wrong');
        setStatus('error');
      }
    },
    [email, name, honeypot, report, request, location, message]
  );

  const showCalendar = view === 'calendar';
  const contentKey = showCalendar ? 'calendar' : status === 'success' ? 'success' : 'form';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop. Above the fixed site header (z-index 80, HomepageHeader.module.css):
              the calendar card is tall enough to reach under it otherwise. */}
          <motion.div
            className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              ref={modalRef}
              className={
                showCalendar
                  // 720px keeps Google's month and times side by side; the frame takes the remaining height.
                  // A phone gets the full height: Google stacks the times below the month there.
                  ? 'relative flex h-[calc(100dvh-2rem)] w-full max-w-[720px] flex-col overflow-hidden rounded-2xl bg-white shadow-xl sm:h-[min(760px,calc(100dvh-2rem))]'
                  : 'relative flex max-h-[calc(100dvh-2rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-xl'
              }
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2, ease: [0.33, 0, 0.2, 1] }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="booking-modal-title"
            >
              <button
                onClick={onClose}
                className="absolute right-3.5 top-3 z-10 grid h-8 w-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-600 sm:right-6 sm:top-5"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Only the content cross-fades on a view switch; the card itself swaps size. */}
              <motion.div
                key={contentKey}
                className="flex min-h-0 flex-1 flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: reduceMotion ? 0 : 0.15 }}
              >
                {showCalendar ? (
                  <CalendarContent onMessage={handleMessage} />
                ) : status === 'success' ? (
                  <SuccessContent
                    manualReview={request?.kind === 'manual-review'}
                    replyEmail={report ? report.replyEmail : email.trim()}
                    onBookCall={handleBookCall}
                  />
                ) : (
                  <FormContent
                    request={request}
                    report={report}
                    location={location}
                    onLocationChange={setLocation}
                    message={message}
                    onMessageChange={setMessage}
                    onBookCall={request?.kind === 'manual-review' ? undefined : handleBookCall}
                    email={email}
                    name={name}
                    honeypot={honeypot}
                    status={status}
                    error={error}
                    onEmailChange={setEmail}
                    onNameChange={setName}
                    onHoneypotChange={setHoneypot}
                    onSubmit={handleSubmit}
                  />
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
